import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getBaseUrl } from '../config';
import { useToast } from './Toast';

// Map common MIME types to file extensions so we can suggest a filename
// when downloading (the list API only returns id/mime/size, not a name).
const MIME_EXTENSIONS = {
  'text/plain': 'txt',
  'text/html': 'html',
  'text/csv': 'csv',
  'application/json': 'json',
  'application/pdf': 'pdf',
  'application/zip': 'zip',
  'application/gzip': 'gz',
  'application/x-tar': 'tar',
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/svg+xml': 'svg',
  'audio/mpeg': 'mp3',
  'audio/wav': 'wav',
  'video/mp4': 'mp4',
  'video/webm': 'webm',
};

const extensionForMime = (mime) => MIME_EXTENSIONS[mime] || 'bin';

// Determine whether a file can be previewed in the browser and how.
// Returns one of: 'image' | 'text' | 'pdf' | 'audio' | 'video' | null
const previewTypeForMime = (mime) => {
  if (!mime) return null;
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('audio/')) return 'audio';
  if (mime.startsWith('video/')) return 'video';
  if (mime === 'application/pdf') return 'pdf';
  if (
    mime.startsWith('text/') ||
    mime === 'application/json' ||
    mime === 'application/xml' ||
    mime === 'application/javascript' ||
    mime.endsWith('+json') ||
    mime.endsWith('+xml')
  ) {
    return 'text';
  }
  return null;
};

const FileUpload = () => {
  const [file, setFile] = useState(null);
  const [fileUrl, setFileUrl] = useState('');
  const [files, setFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesError, setFilesError] = useState('');
  const [sortColumn, setSortColumn] = useState('id');
  const [sortOrder, setSortOrder] = useState('asc');
  const [previewFile, setPreviewFile] = useState(null);
  const [previewText, setPreviewText] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [lastUploadedId, setLastUploadedId] = useState('');
  const showToast = useToast();

  const fetchFiles = useCallback(async () => {
    setFilesLoading(true);
    setFilesError('');
    try {
      const response = await fetch(`${getBaseUrl()}/ops/files`);
      if (!response.ok) {
        throw new Error('Failed to fetch files');
      }
      const data = await response.json();
      setFiles(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
      setFilesError('Failed to load file list');
    } finally {
      setFilesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setFileUrl('');
  };

  const handleUpload = async () => {
    if (!file) {
      showToast('Please select a file', 'warning');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${getBaseUrl()}/files`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data = await response.json();
      const id = data.id;

      const downloadUrl = `${getBaseUrl()}/files/${id}`;

      showToast(`File uploaded successfully! Its id is ${id}`, 'success');
      setFileUrl(downloadUrl);
      setFile(null);
      setLastUploadedId(id);
      fetchFiles();
    } catch (error) {
      console.error(error);
      showToast('File upload failed', 'error');
    }
  };

  // ✅ FIXED DOWNLOAD FUNCTION
  const handleDownload = async () => {
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = file ? file.name : 'downloaded-file';

      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const handleDownloadFile = async (fileItem) => {
    try {
      const response = await fetch(`${getBaseUrl()}/files/${fileItem.id}`);
      if (!response.ok) {
        throw new Error('Download failed');
      }
      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileItem.id}.${extensionForMime(fileItem.mime)}`;

      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      showToast('File download failed', 'error');
    }
  };

  const handleDeleteFile = async (fileItem) => {
    if (!window.confirm(`Delete file "${fileItem.id}"?`)) {
      return;
    }

    try {
      const response = await fetch(`${getBaseUrl()}/ops/file/${fileItem.id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Delete failed');
      }

      showToast(`File "${fileItem.id}" deleted`, 'success');
      fetchFiles();
    } catch (error) {
      console.error('Delete failed:', error);
      showToast('File delete failed', 'error');
    }
  };

  const handlePreview = async (fileItem) => {
    const type = previewTypeForMime(fileItem.mime);
    if (!type) return;

    setPreviewFile(fileItem);
    setPreviewText('');

    if (type === 'text') {
      setPreviewLoading(true);
      try {
        const response = await fetch(`${getBaseUrl()}/files/${fileItem.id}`);
        if (!response.ok) {
          throw new Error('Failed to load preview');
        }
        const text = await response.text();
        setPreviewText(text);
      } catch (error) {
        console.error('Preview failed:', error);
        setPreviewText('Failed to load file content.');
      } finally {
        setPreviewLoading(false);
      }
    }
  };

  const closePreview = () => {
    setPreviewFile(null);
    setPreviewText('');
    setPreviewLoading(false);
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortOrder('asc');
    }
  };

  const sortedFiles = useMemo(() => {
    const sorted = [...files];
    sorted.sort((a, b) => {
      const aVal = a[sortColumn];
      const bVal = b[sortColumn];

      let comparison;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        comparison = aVal - bVal;
      } else {
        comparison = String(aVal ?? '').localeCompare(String(bVal ?? ''));
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

    // Prepend the most recently uploaded file so it appears at the top.
    if (lastUploadedId) {
      const index = sorted.findIndex((f) => f.id === lastUploadedId);
      if (index > 0) {
        const [recent] = sorted.splice(index, 1);
        sorted.unshift(recent);
      }
    }

    return sorted;
  }, [files, sortColumn, sortOrder, lastUploadedId]);

  const formatSize = (bytes) => {
    if (bytes == null) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  };

  const SortIcon = ({ column }) => {
    if (sortColumn !== column) {
      return <i className="bi bi-arrow-down-up has-text-grey-light"></i>;
    }
    return sortOrder === 'asc'
      ? <i className="bi bi-caret-up-fill has-text-primary"></i>
      : <i className="bi bi-caret-down-fill has-text-primary"></i>;
  };

  const renderPreviewContent = () => {
    if (!previewFile) return null;
    const type = previewTypeForMime(previewFile.mime);
    const url = `${getBaseUrl()}/files/${previewFile.id}`;

    switch (type) {
      case 'image':
        return (
          <img
            src={url}
            alt={previewFile.id}
            style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
          />
        );
      case 'audio':
        return <audio controls src={url} style={{ width: '100%' }} />;
      case 'video':
        return <video controls src={url} style={{ maxWidth: '100%', maxHeight: '70vh' }} />;
      case 'pdf':
        return (
          <iframe
            src={url}
            title={previewFile.id}
            style={{ width: '100%', height: '70vh', border: 'none' }}
          />
        );
      case 'text':
        if (previewLoading) {
          return <button className="button is-loading is-static">Loading preview...</button>;
        }
        return (
          <pre
            style={{
              maxHeight: '70vh',
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              background: '#f5f5f5',
              padding: '1rem',
              borderRadius: '4px'
            }}
          >
            {previewText}
          </pre>
        );
      default:
        return <p>This file type cannot be previewed in the browser.</p>;
    }
  };

  return (
    <div className="box" style={{ width: '100%' }}>

      {/* Heading */}
      <h2 className="title is-4">File Upload</h2>

      {/* Instruction */}
      <p className="mb-3">Select a file to upload</p>

      {/* File Input */}
      <div className="field">
        <div className="file has-name is-fullwidth">
          <label className="file-label">
            <input
              className="file-input"
              type="file"
              onChange={handleFileChange}
            />
            <span className="file-cta">
              <span className="file-label">Choose file</span>
            </span>
            <span className="file-name">
              {file ? file.name : 'No file selected'}
            </span>
          </label>
        </div>
      </div>

      {/* Upload Button */}
      <button className="button is-primary mt-2" onClick={handleUpload}>
        Upload
      </button>

      {/* Download link */}
      {fileUrl && (
        <p style={{ marginTop: '1rem' }}>
          Download your file{' '}
          <span
            onClick={handleDownload}
            style={{
              color: '#3273dc',
              fontWeight: '600',
              cursor: 'pointer',
              textDecoration: 'underline'
            }}
          >
            here
          </span>
        </p>
      )}

      {/* Uploaded Files List */}
      <hr />
      <h3 className="title is-5">Uploaded Files</h3>

      {filesLoading && (
        <div className="notification is-info is-light">
          <button className="button is-loading is-static">Loading files...</button>
        </div>
      )}

      {filesError && (
        <div className="notification is-danger is-light">
          Error: {filesError}
        </div>
      )}

      {!filesLoading && !filesError && files.length === 0 && (
        <div className="notification is-warning is-light">
          No files uploaded yet.
        </div>
      )}

      {!filesLoading && !filesError && files.length > 0 && (
        <div className="table-container" style={{ overflowX: 'auto' }}>
          <table className="table is-striped is-hoverable is-fullwidth">
            <thead>
              <tr>
                <th
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('id')}
                >
                  <span className="is-flex is-align-items-center">
                    ID <span className="ml-2"><SortIcon column="id" /></span>
                  </span>
                </th>
                <th
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('mime')}
                >
                  <span className="is-flex is-align-items-center">
                    Content Type <span className="ml-2"><SortIcon column="mime" /></span>
                  </span>
                </th>
                <th
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('size')}
                >
                  <span className="is-flex is-align-items-center">
                    Size <span className="ml-2"><SortIcon column="size" /></span>
                  </span>
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedFiles.map((fileItem) => (
                <tr
                  key={fileItem.id}
                  className={fileItem.id === lastUploadedId ? 'is-selected' : ''}
                >
                  <td style={{ wordBreak: 'break-all' }}>
                    {fileItem.id}
                    {fileItem.id === lastUploadedId && (
                      <span className="tag is-success is-light ml-2">New</span>
                    )}
                  </td>
                  <td>{fileItem.mime}</td>
                  <td>{formatSize(fileItem.size)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <div className="buttons are-small">
                      {previewTypeForMime(fileItem.mime) && (
                        <button
                          type="button"
                          className="backup-icon-button"
                          title="Preview"
                          onClick={() => handlePreview(fileItem)}
                        >
                          <i className="bi bi-eye"></i>
                        </button>
                      )}
                      <button
                        type="button"
                        className="backup-icon-button"
                        title="Download"
                        onClick={() => handleDownloadFile(fileItem)}
                      >
                        <i className="bi bi-download"></i>
                      </button>
                      <button
                        type="button"
                        className="backup-icon-button"
                        title="Delete"
                        onClick={() => handleDeleteFile(fileItem)}
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Preview Modal */}
      {previewFile && (
        <div className={`modal is-active`}>
          <div className="modal-background" onClick={closePreview}></div>
          <div className="modal-card" style={{ width: '90%', maxWidth: '960px' }}>
            <header className="modal-card-head">
              <p className="modal-card-title" style={{ wordBreak: 'break-all' }}>
                {previewFile.id}
              </p>
              <button className="delete" aria-label="close" onClick={closePreview}></button>
            </header>
            <section className="modal-card-body">
              <p className="mb-3">
                <span className="tag is-light file-meta-tag">{previewFile.mime}</span>{' '}
                <span className="tag is-light file-meta-tag">{formatSize(previewFile.size)}</span>
              </p>
              {renderPreviewContent()}
            </section>
            <footer className="modal-card-foot">
              <div className="buttons">
                <button className="button is-primary" onClick={() => handleDownloadFile(previewFile)}>
                  <span className="icon">
                    <i className="bi bi-download"></i>
                  </span>
                  <span>Download</span>
                </button>
                <button className="button" onClick={closePreview}>Close</button>
              </div>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
